import { useEffect, useState } from 'react';

import { FIREBASE } from '../firebase';
import { Category, logError } from '../bot/sentry-iframe';

/** Shape of data returned from `useCustomBotIcon.ts` hook */
export type CustomBotIconHook = {
  url: string | null;
  status: 'idle' | 'loading' | 'success' | 'error';
};

/** Get Custom Bot Icon image. Use this in `Initialize.tsx` file,
 * so that we have the bot icon url ready to load as soon as the
 * chat window is opened. */
export function useCustomBotIcon(botIconFileName?: string): CustomBotIconHook {
  const [state, setState] = useState<CustomBotIconHook>({
    status: 'idle',
    url: null,
  });

  // Get Bot Icon URL from Firebase
  useEffect(() => {
    if (botIconFileName) {
      const fetchImage = async () => {
        setState({ status: 'loading', url: null });
        const url = await getFirebaseStorageImageURL(
          `bot_icons/${botIconFileName}`
        );
        if (url) {
          setState({ status: 'success', url });
        } else {
          setState({ status: 'error', url: null });
        }
      };
      fetchImage();
    } else {
      // This site doesn't use a custom icon
      setState({ status: 'idle', url: null });
    }
  }, [botIconFileName]);

  return state;
}

/** Get the download URL for an image stored in Firebase Storage
 *
 * @param {string} imagePath - must include the folder and complete file name.
 * For example: `bot_icons/demo.png`
 */
const getFirebaseStorageImageURL = async (imagePath: string) => {
  try {
    const imageRef = FIREBASE.storage.ref().child(imagePath);
    return (await imageRef.getDownloadURL()) as string;
  } catch (error) {
    logError(error, {
      message: 'Error getting download URL from Firebase Storage',
      category: Category.Sites,
      data: { imagePath },
    });
    return null;
  }
};
